import CwLoadModules from '@customjs/module-loader.js';

function officeText(key, fallback) {
    const labels = (typeof ThemeURLS !== 'undefined' && ThemeURLS.i18n) ? ThemeURLS.i18n : {};
    return labels[key] || fallback;
}

const CwThemeFramework = {
    async generalFns() {
        const office = document.querySelector('.select-office');
        if (office) {
            this.loadLocations(office);
            office.addEventListener('change', () => this.loadLocations(office));
        }
        //(For Contact page Location Widget)
        const officeContact = document.querySelector('.select-office-contact');
        if (officeContact) {
            this.loadLocationsContact(officeContact);
            officeContact.addEventListener('change', () => this.loadLocationsContact(officeContact));
        }
        this.stickyHeader();
        this.teamFilter();
        this.tocActiveItem();
        this.attorneyPagination();
        this.pieChart();     
        this.progressBars();        
        this.drunkDriving();
        this.professionalConferences();
    },

    /**
     * Sticky Header
     *
     * Uses `position: sticky` (no layout shift) and an IntersectionObserver
     * on a sentinel element to add the `is-stuck` class once the header has
     * scrolled to the top, enabling shadow/visual changes without CLS.
     */
    stickyHeader() {
        const header = document.querySelector('.header--sticky');
        if (!header) return;

        const setStickyOffset = () => {
            const height = Math.ceil(header.getBoundingClientRect().height);
            const gap = height ? 24 : 0;

            document.documentElement.style.setProperty('--sticky-header-offset', `${height + gap}px`);
        };

        setStickyOffset();
        window.addEventListener('resize', setStickyOffset);

        const sentinel = document.querySelector('.sticky-sentinel');
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                header.classList.toggle('is-stuck', !entry.isIntersecting);
            },
            { threshold: [0] }
        );

        observer.observe(sentinel);
    },

    /**
     * Persist the selected TOC item until another item is clicked.
     */
    tocActiveItem() {
        if (!document.body.classList.contains('page-template-auto-insurance')) {
            return;
        }

        const setCurrent = (href) => {
            if (!href) {
                return;
            }

            document.querySelectorAll('.toc-table-list a').forEach((link) => {
                link.closest('li')?.classList.toggle('is-current', link.getAttribute('href') === href);
            });
        };

        document.addEventListener('click', (event) => {
            const link = event.target.closest('.toc-table-list a');
            if (!link) {
                return;
            }

            setCurrent(link.getAttribute('href'));
        });

        const applyDefault = () => {
            if (window.location.hash) {
                setCurrent(window.location.hash);
                return;
            }

            const firstLink = document.querySelector('.toc-table-list li:first-child a');
            if (firstLink) {
                setCurrent(firstLink.getAttribute('href'));
            }
        };

        if (document.querySelector('.toc-table-list')) {
            applyDefault();
            return;
        }

        const observer = new MutationObserver(() => {
            if (document.querySelector('.toc-table-list')) {
                applyDefault();
                observer.disconnect();
            }
        });

        observer.observe(document.body, { childList: true, subtree: true });
    },

    /**
     * Team filter
     */
    teamFilter() {
        const filterButtons = document.querySelectorAll('.filter-btn');
        const cards = document.querySelectorAll('.card');

        filterButtons.forEach(button => {
          button.addEventListener('click', () => {
            // Toggle active button style
            filterButtons.forEach(btn => btn.classList.remove('active'));
            button.classList.add('active');

            const filterValue = button.getAttribute('data-filter');

            // Filter cards by category
            cards.forEach(card => {
              const cardCategory = card.getAttribute('data-category');
              if (filterValue === 'all' || cardCategory === filterValue) {
                card.classList.remove('hide');
              } else {
                card.classList.add('hide');
              }
            });
          });
        });
    },
    
    /**
     * Progress Bars
     */
    progressBars() {
        document.addEventListener('DOMContentLoaded', () => {
          const skillsSection = document.getElementById('skills');

          if (!skillsSection) return;

          const observer = new IntersectionObserver((entries, observerInstance) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                // Find all .skillbar elements inside #skills
                const skillbars = skillsSection.querySelectorAll('.skillbar');

                skillbars.forEach(skillbar => {
                  const bar = skillbar.querySelector('.skillbar-bar');
                  if (bar) {
                    // Replaces jQuery(this).attr('data-percent')
                    const percent = skillbar.getAttribute('data-percent');
                    
                    // Triggers the 2-second CSS transition animation
                    bar.style.width = percent;
                  }
                });

                // Stop observing once triggered so it only runs once on scroll
                observerInstance.unobserve(entry.target);
              }
            });
          }, {
            // Triggers as soon as the top of #skills enters the bottom of the viewport
            rootMargin: '0px'
          });

          observer.observe(skillsSection);
        });
    },
    
    /**
     * Pie Bar
     */
    pieChart() {
      document.addEventListener('DOMContentLoaded', () => {
      const pieChart = document.getElementById('pieChart');

      const slices = [
        { color: '#fbb024', percentage: 67.2 },
        { color: '#d8b574', percentage: 18.0 },
        { color: '#a0885b', percentage: 10.0 },
        { color: '#5f7893', percentage: 3.3 },
        { color: '#b9d4ec', percentage: 0.5 },
        { color: '#73b1f4', percentage: 1.0 }
      ];

      const startAnimation = () => {
        const duration = 2000;
        const startTime = performance.now();

        function animateChart(currentTime) {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easedProgress = 1 - Math.pow(1 - progress, 3); // Ease-out curve

          let currentAngle = 0;
          const gradientStops = [];

          slices.forEach((slice) => {
            const sliceAngle = (slice.percentage / 100) * 360 * easedProgress;
            const startAngle = currentAngle;
            const endAngle = currentAngle + sliceAngle;

            gradientStops.push(`${slice.color} ${startAngle}deg ${endAngle}deg`);
            currentAngle = endAngle;
          });

          pieChart.style.background = `conic-gradient(${gradientStops.join(', ')})`;

          if (progress < 1) {
            requestAnimationFrame(animateChart);
          }
        }

        requestAnimationFrame(animateChart);
      };

      // Trigger animation only when scrolling the chart div into view
      const observer = new IntersectionObserver((entries, observerInstance) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            startAnimation();
            observerInstance.unobserve(entry.target); // Ensures it only runs once
          }
        });
      }, { threshold: 0.5 }); // Triggers when 50% of the div is visible

      if (pieChart) {
        observer.observe(pieChart);
      }
    });
    },

    /**
     * Feedback drunkDriving Pagination
     */
    drunkDriving() {
        const ITEMS_PER_PAGE = 10;
        const viewMoreBtn = document.getElementById('viewMoreBtn');

        if (viewMoreBtn) {        
        viewMoreBtn.addEventListener('click', () => {
          // Find hidden items
          const hiddenCards = document.querySelectorAll('.testimonial-card:not(.is-visible)');
          
          // Reveal the next batch
          Array.from(hiddenCards).slice(0, ITEMS_PER_PAGE).forEach(card => {
            card.classList.add('is-visible');
          });

          // Hide the button when no hidden items remain
          if (document.querySelectorAll('.testimonial-card:not(.is-visible)').length === 0) {
            viewMoreBtn.style.display = 'none';
          }
        });
      }
    },

    /**
     * Feedback professionalConferences Pagination
     */
    professionalConferences() {
        const ITEMS_PER = 10;
        const conferencesBtn = document.getElementById('conferencesBtn');
        if(conferencesBtn) {
        conferencesBtn.addEventListener('click', () => {
          // Find hidden items
          const hiddenCards = document.querySelectorAll('.conferences-card:not(.is-visible)');
          
          // Reveal the next batch
          Array.from(hiddenCards).slice(0, ITEMS_PER).forEach(card => {
            card.classList.add('is-visible');
          });

          // Hide the button when no hidden items remain
          if (document.querySelectorAll('.conferences-card:not(.is-visible)').length === 0) {
            conferencesBtn.style.display = 'none';
          }
        });
      }
    },

    /**
     * Attorney Pagination
     */
    attorneyPagination() {
        document.addEventListener('DOMContentLoaded', function () {
            if (document.getElementById('pagingBox')) {
                const showPerPage = 10;
                const pagingBox = document.getElementById('pagingBox');
                const items = Array.from(pagingBox.children);
                const numberOfItems = items.length;
                const numberOfPages = Math.ceil(numberOfItems / showPerPage);

                const currentPageInput = document.getElementById('current_page');
                const showPerPageInput = document.getElementById('show_per_page');
                const pageNavigation = document.getElementById('page_navigation');

                if (currentPageInput) currentPageInput.value = 0;
                if (showPerPageInput) showPerPageInput.value = showPerPage;

                let navigationHtml = '<a class="previous_link" href="#">Previous</a>';

                for (let i = 0; i < numberOfPages; i++) {
                    navigationHtml += `<a class="page_link" href="#" data-page="${i}">${i + 1}</a>`;
                }
                navigationHtml += '<a class="next_link" href="#">Next</a>';

                pageNavigation.innerHTML = navigationHtml;

                const firstPageLink = pageNavigation.querySelector('.page_link');
                if (firstPageLink) firstPageLink.classList.add('active_page');

                items.forEach((item) => (item.style.display = 'none'));
                items.slice(0, showPerPage).forEach((item) => (item.style.display = 'block'));
                updateNavButtons(0);

                pageNavigation.addEventListener('click', function (e) {
                    e.preventDefault();
                    const target = e.target;

                    if (target.classList.contains('inactive')) {
                        return;
                    }

                    if (target.classList.contains('previous_link')) {
                        previous();
                    } else if (target.classList.contains('next_link')) {
                        next();
                    } else if (target.classList.contains('page_link')) {
                        const pageNum = parseInt(target.getAttribute('data-page'), 10);
                        goToPage(pageNum);
                    }
                });
            }
        });

        function updateNavButtons(pageNum) {
            const pageNavigation = document.getElementById('page_navigation');
            if (!pageNavigation) return;

            const previousLink = pageNavigation.querySelector('.previous_link');
            const nextLink = pageNavigation.querySelector('.next_link');
            const numberOfPages = pageNavigation.querySelectorAll('.page_link').length;
            const isFirstPage = pageNum <= 0;
            const isLastPage = numberOfPages <= 1 || pageNum >= numberOfPages - 1;

            if (previousLink) {
                previousLink.classList.toggle('inactive', isFirstPage);
                previousLink.setAttribute('aria-disabled', isFirstPage ? 'true' : 'false');
            }

            if (nextLink) {
                nextLink.classList.toggle('inactive', isLastPage);
                nextLink.setAttribute('aria-disabled', isLastPage ? 'true' : 'false');
            }
        }

        function previous() {
            const currentPageInput = document.getElementById('current_page');
            const newPage = parseInt(currentPageInput.value, 10) - 1;
            const activePage = document.querySelector('.active_page');

            if (activePage && activePage.previousElementSibling && activePage.previousElementSibling.classList.contains('page_link')) {
                goToPage(newPage);
            }
        }

        function next() {
            const currentPageInput = document.getElementById('current_page');
            const newPage = parseInt(currentPageInput.value, 10) + 1;
            const activePage = document.querySelector('.active_page');

            if (activePage && activePage.nextElementSibling && activePage.nextElementSibling.classList.contains('page_link')) {
                goToPage(newPage);
            }
        }

        function goToPage(pageNum) {
            const showPerPageInput = document.getElementById('show_per_page');
            const showPerPage = parseInt(showPerPageInput.value, 10);
            const pagingBox = document.getElementById('pagingBox');
            const items = Array.from(pagingBox.children);

            const startFrom = pageNum * showPerPage;
            const endOn = startFrom + showPerPage;

            items.forEach((item) => (item.style.display = 'none'));
            items.slice(startFrom, endOn).forEach((item) => (item.style.display = 'block'));

            const allPageLinks = document.querySelectorAll('.page_link');
            allPageLinks.forEach((link) => link.classList.remove('active_page'));

            const targetLink = document.querySelector(`.page_link[data-page="${pageNum}"]`);
            if (targetLink) targetLink.classList.add('active_page');

            const currentPageInput = document.getElementById('current_page');
            if (currentPageInput) currentPageInput.value = pageNum;

            updateNavButtons(pageNum);
        }
    },
        
    /**
     * Load Locations
     */
    async loadLocations(locationID) {
        if (!locationID?.value) {
            return;
        }

        const officeBox = document.querySelector('.office-info');
        const officeMapBox = document.querySelector('.office-map');
        const officeHours = document.querySelector('.opening-hours');

        if (!officeBox) {
            return;
        }

        CwLoadModules.modals().then((modals) => {
            const officeHoursModal = new modals('.cw-modal');
            officeHoursModal.init();
        });

        const homeURL = (typeof ThemeURLS !== 'undefined' && ThemeURLS.homeURL)
            ? ThemeURLS.homeURL.replace(/\/$/, '')
            : '';
        const apiURL = `${homeURL}/api/location/${locationID.value}/`;

        let officeDetails;

        try {
            const officeInfo = await fetch(apiURL);

            if (!officeInfo.ok) {
                console.error('Location API request failed.', officeInfo.status);
                return;
            }

            const responseText = await officeInfo.text();
            const jsonStart = responseText.indexOf('{"success"');

            if (jsonStart === -1) {
                console.error('Location API returned a non-JSON response.', responseText.slice(0, 200));
                return;
            }

            officeDetails = JSON.parse(responseText.slice(jsonStart));
        } catch (error) {
            console.error('Failed to load location details.', error);
            return;
        }

        if (officeDetails.success) {
                const {
                    firmName,
                    directionURL,
                    city,
                    state,
                    zip,
                    location_address_1,
                    location_address_2,
                    phone,
                    email,
                    toll_free_number,
                    location_fax,
                    footer_map,
                    monday_hours,
                    tuesday_hours,
                    wednesday_hours,
                    thursday_hours,
                    friday_hours,
                    saturday_hours,
                    sunday_hours,
                } = officeDetails.data;

                const isFooterOffice = Boolean(officeBox.closest('footer'));
                const firmNameHTML = firmName && !isFooterOffice ?
                    `<strong>${firmName}</strong>` :
                    '';
                const address1HTML = location_address_1 ?
                    `<span>${location_address_1}</span>` :
                    '';
                const address2HTML = location_address_2 ?
                    `<span>${location_address_2}</span>` :
                    '';
                const phoneHTML = phone ?
                    `<a href="tel:${phone}" class="ctm-no-swap" aria-label="Phone">${phone}</a>` :
                    '';
                const emailHTML = email ?
                    `<a href="mailto:${email}" aria-label="Email Address">${email}</a>` :
                    '';
                const officeInfoButtons = `<div class="office-info__buttons">
                                       <button class="btn cw-modal" data-cw-target="#hoursModal" id="office-hours">${officeText('hours', 'Hours')}</button>
                    <a href="${directionURL}" target="_blank" aria-label="${officeText('directionLink', 'Direction Link')}">${officeText('directions', 'Directions')}</a>

               </div>`;

                const additionalCommunitiesLink = (typeof ThemeURLS !== 'undefined' && ThemeURLS.additionalCommunitiesLink)
                    ? ThemeURLS.additionalCommunitiesLink
                    : null;

                let additionalCommunitiesHTML = '';
                if (additionalCommunitiesLink?.url && additionalCommunitiesLink?.title) {
                    additionalCommunitiesHTML = `
                        <a href="${additionalCommunitiesLink.url}"
                           class="additional-communities-link"
                           ${additionalCommunitiesLink.target ? `target="${additionalCommunitiesLink.target}"` : ''}>
                            ${additionalCommunitiesLink.title}
                        </a>
                    `;
                }

                // The firmName is only visible inside the address block, not in the footer map's alt
                const footerMapHTML = footer_map ?
                    `<figure><a href="${directionURL}" target="_blank" aria-label="Direction Link"><img src="${footer_map}" alt="" /></a></figure>` :
                    '';

                const address = `${
                    firmNameHTML
                }
               <address>
                   ${address1HTML}
                   ${address2HTML}<br>
                   <span>${city}, ${state}, ${zip}</span><br>
               </address>
               ${phoneHTML}<br>
               ${emailHTML}${officeInfoButtons}
               ${additionalCommunitiesHTML}`;

                officeBox.innerHTML = address;

                if (officeMapBox) {
                    officeMapBox.innerHTML = footerMapHTML;
                }

                const mondayHoursHTML = monday_hours ?
                    `<tr><td class="day">${officeText('monday', 'Monday')}</td><td class="time"><span>${monday_hours}</span></td></tr>` :
                    '';
                const tuesdayHoursHTML = tuesday_hours ?
                    `<tr><td class="day">${officeText('tuesday', 'Tuesday')}</td><td class="time"><span>${tuesday_hours}</span></td></tr>` :
                    '';
                const wednesdayHoursHTML = wednesday_hours ?
                    `<tr><td class="day">${officeText('wednesday', 'Wednesday')}</td><td class="time"><span>${wednesday_hours}</span></td></tr>` :
                    '';
                const thursdayHoursHTML = thursday_hours ?
                    `<tr><td class="day">${officeText('thursday', 'Thursday')}</td><td class="time"><span>${thursday_hours}</span></td></tr>` :
                    '';
                const fridayHoursHTML = friday_hours ?
                    `<tr><td class="day">${officeText('friday', 'Friday')}</td><td class="time"><span>${friday_hours}</span></td></tr>` :
                    '';
                const saturdayHoursHTML = saturday_hours ?
                    `<tr><td class="day">${officeText('saturday', 'Saturday')}</td><td class="time"><span>${saturday_hours}</span></td></tr>` :
                    '';
                const sundayHoursHTML = sunday_hours ?
                    `<tr><td class="day">${officeText('sunday', 'Sunday')}</td><td class="time"><span>${sunday_hours}</span></td></tr>` :
                    '';

                const hoursOffice = `<table>
                              <tbody>
                              ${mondayHoursHTML}
                              ${tuesdayHoursHTML}
                              ${wednesdayHoursHTML}
                              ${thursdayHoursHTML}
                              ${fridayHoursHTML}
                              ${saturdayHoursHTML}
                              ${sundayHoursHTML}
                              </tbody>
                            </table>`;

                if (officeHours) {
                    officeHours.innerHTML = hoursOffice;
                }
            }
    },

    /**
    * Load Locations (For Contact page Location Widget)
    */
    async loadLocationsContact(contactLocationID) {
        if (contactLocationID) {
            CwLoadModules.modals().then((modals) => {
                const officeHours = new modals('.cw-modal');
                officeHours.init();
            });            
            const apiURL = `/api/location/${contactLocationID.value}/`;                                 
            const officeBox = document.querySelector('.office-info-contact');
            const officeMap = document.querySelector('.office-map-contact');
            const officeHours = document.querySelector('.opening-hours');
            const officeInfo = await fetch(apiURL);
            const officeDetails = await officeInfo.json();

            if (officeDetails.success) {
                const {
                    firmName,
                    directionURL,
                    infoURL,
                    city,
                    state,
                    zip,
                    location_address_1,
                    location_address_2,
                    phone,
                    email,
                    toll_free_number,
                    location_fax,
                    footer_map,
                    monday_hours,
                    tuesday_hours,
                    wednesday_hours,
                    thursday_hours,
                    friday_hours,
                    saturday_hours,
                    sunday_hours,
                } = officeDetails.data;

                const firmNameHTML = firmName ? `<strong>${firmName}</strong>` : '';
                const address1HTML = location_address_1 ?
                    `<span>${location_address_1}</span><br />` :
                    '';
                const address2HTML = location_address_2 ?
                    `<span>${location_address_2}</span>` :
                    '';
                const phoneHTML = phone ?
                    `<a href="tel:${phone}" class="ctm-no-swap" aria-label="Phone">${phone}</a>` :
                    '';
                const toll_free_numberHTML = toll_free_number ?
                    `<a href="tel:${toll_free_number}" aria-label="Phone">Toll Free: ${toll_free_number}</a>` :
                    '';    
                const emailHTML = email ?
                    `<a href="mailto:${email}" aria-label="Email Address">${email}</a>` :
                    '';
                const officeInfoButtons = `<div class="office-info__buttons">
                                       <button class="btn cw-modal" data-cw-target="#hoursModal" id="office-hours">${officeText('hours', 'Hours')}</button>
                    <a href="${directionURL}" target="_blank" aria-label="${officeText('directionLink', 'Direction Link')}">${officeText('directions', 'Directions')}</a>

               </div>`;

                // Use firmName in both the address info and the footer_map alt
                const footerMapHTML = footer_map ?
                    `<figure><a href="${directionURL}" target="_blank" aria-label="Direction Link"><img src="${footer_map}" alt="${firmName}" />
                     </a></figure>` :
                    '';

                const address = `${
                    firmNameHTML
                }
               <address>
                   ${address1HTML}
                   ${address2HTML}
                   <span>${city}, ${state}, ${zip}</span>
               </address>
               ${phoneHTML}
               ${toll_free_numberHTML}<br>
               ${emailHTML}${officeInfoButtons}
               ${footerMapHTML}`;
                officeBox.innerHTML = address;

                const mondayHoursHTML = monday_hours ?
                    `<tr><td class="day">${officeText('monday', 'Monday')}</td><td class="time"><span>${monday_hours}</span></td></tr>` :
                    '';
                const tuesdayHoursHTML = tuesday_hours ?
                    `<tr><td class="day">${officeText('tuesday', 'Tuesday')}</td><td class="time"><span>${tuesday_hours}</span></td></tr>` :
                    '';
                const wednesdayHoursHTML = wednesday_hours ?
                    `<tr><td class="day">${officeText('wednesday', 'Wednesday')}</td><td class="time"><span>${wednesday_hours}</span></td></tr>` :
                    '';
                const thursdayHoursHTML = thursday_hours ?
                    `<tr><td class="day">${officeText('thursday', 'Thursday')}</td><td class="time"><span>${thursday_hours}</span></td></tr>` :
                    '';
                const fridayHoursHTML = friday_hours ?
                    `<tr><td class="day">${officeText('friday', 'Friday')}</td><td class="time"><span>${friday_hours}</span></td></tr>` :
                    '';
                const saturdayHoursHTML = saturday_hours ?
                    `<tr><td class="day">${officeText('saturday', 'Saturday')}</td><td class="time"><span>${saturday_hours}</span></td></tr>` :
                    '';
                const sundayHoursHTML = sunday_hours ?
                    `<tr><td class="day">${officeText('sunday', 'Sunday')}</td><td class="time"><span>${sunday_hours}</span></td></tr>` :
                    '';

                const hoursOffice = `<table>
                              <tbody>
                              ${mondayHoursHTML}
                              ${tuesdayHoursHTML}
                              ${wednesdayHoursHTML}
                              ${thursdayHoursHTML}
                              ${fridayHoursHTML}
                              ${saturdayHoursHTML}
                              ${sundayHoursHTML}
                              </tbody>
                            </table>`;

                // Hours Modal
                officeHours.innerHTML = hoursOffice;
            }
        }
    },
    /**
     * Mobile Menu
     */
    mobileMenu() {
        const mobileButton = document.querySelector('.mobile-button');
        const mobileMenuElement = document.querySelector('#mmenu');
        const mobileChecker = document.querySelector('.mm-ocd');

        if (mobileButton && mobileMenuElement) {
            // Call mmenujs module
            CwLoadModules.mmenu().then((MmenuLight) => {
                if (!mobileChecker) {
                    const menu = new MmenuLight(mobileMenuElement);
                    menu.navigation();
                    const drawer = menu.offcanvas();

                    // Register event for listening to clicks
                    mobileButton.addEventListener('click', () => drawer.open());

                    /**
                     * Close Button
                     */
                    const closeButton = document.querySelector('.close__mobileMenu');

                    closeButton.addEventListener('click', () => drawer.close());

                    /**
                     *  Appending a click option for link tags
                     * with a hash(#) symbol at the beginning of the href
                     * */

                    const aHashTag = mobileMenuElement.querySelectorAll('a[href="#"]');
                    aHashTag.forEach((tag) => {
                        tag.addEventListener('click', (event) => {
                            event.target.parentNode.click();
                            event.preventDefault();
                        });
                    });
                }
            });
        }
    },
};

export default CwThemeFramework;